import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Modal, message, notification } from 'antd';
import { useDebounce } from 'usehooks-ts';

import { selectAccountOrgs, selectActiveOrg } from 'shared/store/auth-slice';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  activateVisitor,
  deactivateVisitor,
  fetchMoreContextualRecordings,
  startCall,
  endCall,
  updateContextualRecordingToPlay,
} from 'shared/store/dashboard-slice';
import { selectActiveVisitor } from 'shared/store/shared-selectors';
import {
  selectHaveRealtimeVisitorsLoaded,
  kickAgentFromVisitor,
  pauseVisitorAssociation,
  selectCallStatus,
  selectTakeoverStatus,
  selectRealtimeIntendedVisitor,
} from 'shared/store/realtime-slice';
import { changeActiveOrg } from 'shared/store/shared-actions';
import { makeLog } from 'shared/utils/log';
import { getMySbid } from 'shared/utils/helpers';
import { usePrevious } from './misc';
import { selectIsTouring } from 'shared/store/tour-slice';
import { Gateway, VisitorContextualRecording } from 'types';
import { useIsGatewayActivated } from 'shared/utils/gateways';
import { validate } from 'uuid';
import { contactSales } from 'shared/utils/sales';

const log = makeLog('hooks/visitor');

export const CALL_PARAM = 'call';
export const AUDIO_CALL_PARAM = 'call-audio';

/**
 * Handle all of the minutia of browsing a visitor.
 * 1. Activates the visitor on mount, deactivates on dismount.
 * 2. Redirects to the correct URL if this is a deduplicated visitor.
 * 3. Switches to the correct org if it doesn't match our visitor's org.
 */
export const useActiveVisitorViewHandling = () => {
  const dispatch = useAppDispatch();
  const { replace } = useHistory();
  const { pathname } = useLocation();

  const { visitorId: urlVisitorId } = useParams<{ visitorId: string }>();

  const activeVisitor = useAppSelector(selectActiveVisitor);
  const activeOrg = useAppSelector(selectActiveOrg);
  const accountOrgs = useAppSelector(selectAccountOrgs);
  const activeVisitorId = useAppSelector(s => s.dashboard.activeVisitorId);
  const realtimeVisitor = useAppSelector(selectRealtimeIntendedVisitor);
  const isTouring = useAppSelector(selectIsTouring);

  const replacedSbid = realtimeVisitor?.replacedSbid;
  const realtimeSbid = realtimeVisitor?.sbid;

  const [debouncedActivateVisitorId, setDebouncedActivateVisitorId] = useState('');
  const lastVisitorChangeTimeRef = useRef(0);
  const debounceActivateVisitorTimeoutRef = useRef(0);

  // Activate visitor on visitorId URL param change
  // * Try to always use the URL as SoT and not dispatch activateVisitor from elsewhere
  useEffect(() => {
    let thunkPromise: { abort: () => void } | undefined;

    if (urlVisitorId && urlVisitorId !== activeVisitorId) {
      (async () => {
        const isSwitch = !!(
          replacedSbid &&
          replacedSbid === activeVisitorId &&
          urlVisitorId === realtimeSbid
        );
        log.debug(
          `URL visitorId parameter ${
            isSwitch ? 'SWITCHED (same visitor, different sbid)' : 'CHANGED'
          } (sbid=${urlVisitorId}, oldSbid=${activeVisitorId}), will attempt to activate`,
        );
        if (!isSwitch) {
          const DEBOUNCE = 300;
          // Debounce non-switch changes
          if (Date.now() - lastVisitorChangeTimeRef.current < DEBOUNCE) {
            log.debug(
              `Last vistiorId activated too recently (within ${DEBOUNCE}ms), will wait before activating ${urlVisitorId}`,
            );
            clearTimeout(debounceActivateVisitorTimeoutRef.current);
            debounceActivateVisitorTimeoutRef.current = window.setTimeout(() => {
              setDebouncedActivateVisitorId(urlVisitorId);
            }, DEBOUNCE);
            lastVisitorChangeTimeRef.current = Date.now();
            return;
          } else {
            lastVisitorChangeTimeRef.current = Date.now();
          }

          log.debug('Will deactivate active visitor before activating new');
          await dispatch(deactivateVisitor());
        } else {
          // Always clear the debounce timeout if we get this far
          clearTimeout(debounceActivateVisitorTimeoutRef.current);
        }

        thunkPromise = dispatch(activateVisitor({ visitorId: urlVisitorId, isSwitch }));
      })();

      return () => {
        if (thunkPromise) {
          thunkPromise.abort();
        }
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, urlVisitorId]);

  // Debounced activation
  useEffect(() => {
    let thunkPromise: { abort: () => void } | undefined;
    (async () => {
      if (
        activeVisitorId &&
        debouncedActivateVisitorId &&
        debouncedActivateVisitorId === urlVisitorId &&
        urlVisitorId !== activeVisitorId
      ) {
        log.debug(
          `[debounced] Will deactivate active visitor before activating new (active=${activeVisitorId}, new=${urlVisitorId})`,
        );
        await dispatch(deactivateVisitor());
        thunkPromise = dispatch(
          activateVisitor({ visitorId: urlVisitorId, isSwitch: false }),
        );
      }
      setDebouncedActivateVisitorId(''); // clear it
    })();

    return () => {
      if (thunkPromise) {
        thunkPromise.abort();
      }
    };
  }, [debouncedActivateVisitorId, urlVisitorId, activeVisitorId, dispatch]);

  // Deactivate on unmount
  useEffect(() => {
    return () => {
      dispatch(deactivateVisitor());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      activeVisitorId &&
      activeVisitorId === replacedSbid &&
      pathname.indexOf(replacedSbid) > -1 &&
      realtimeSbid
    ) {
      log.debug(
        `Replacing URL location with swapped id for active visitor (oldSbid=${replacedSbid}, newSbid=${realtimeSbid})`,
      );
      replace(pathname.replace(replacedSbid, realtimeSbid));
    }
  }, [activeVisitorId, replacedSbid, pathname, realtimeSbid, replace]);

  // If the visitor's org id doesn't match our current org id, switch us to that org
  useEffect(() => {
    // Don't check while on product tour
    if (isTouring) return;
    if (activeVisitor?.orgId && activeOrg?.id && activeVisitor.orgId !== activeOrg.id) {
      const newOrg = accountOrgs.find(o => o.id === activeVisitor.orgId);
      if (newOrg) {
        dispatch(changeActiveOrg(activeVisitor.orgId));
        message.info(`Switched organization to "${newOrg.name}" for this visitor`);
      } else {
        message.warn(
          "Visitor belongs to an org you don't have access to. Refresh and try again.",
        );
      }
    }
  }, [activeVisitor, activeOrg, accountOrgs, dispatch, isTouring]);
};

export type ActiveVisitorStatusIcon = 'spinner' | 'failed' | 'left' | 'attention';

/**
 * Provide a bunch of state around connecting to a visitor.
 */
export const useActiveVisitorState = (visitorId: string, isUsingRrweb = true) => {
  const dispatch = useAppDispatch();
  const activeVisitor = useAppSelector(selectActiveVisitor);
  const {
    isAwaitingInitialSnapshot,
    activateVisitorError,
    isAwaitingReplayerFirstRender,
  } = useAppSelector(state => state.dashboard);
  const { isConnectedToPeer, associationStatus, callStatus } = useAppSelector(
    state => state.realtime,
  );
  const haveVisitorsLoaded = useAppSelector(selectHaveRealtimeVisitorsLoaded);
  const isTouring = useAppSelector(selectIsTouring);
  const [hasConnectedToPeer, setHasConnectedToPeer] = useState(false);
  const [isKickingAdmin, setIsKickingAdmin] = useState(false);
  const [isActiveVisitorLeft, setIsActiveVisitorLeft] = useState(false);
  const isViewingSelf = visitorId === getMySbid();
  const isShowingSpyViewingsUsageLimit = useAppSelector(
    s => s.dashboard.isShowingSpyViewingsUsageLimit,
  );
  const prevActiveVisitorId = usePrevious(activeVisitor?.id);

  const isOnline = !!activeVisitor?.sid;
  const wasOnline = usePrevious(isOnline);

  // This mitigates the risk of ending a call with a visitor who is still on the site but has
  // temporarily disconnected or changed sid due to a identity change.
  const isActiveVisitorLeftDebounced = useDebounce(isActiveVisitorLeft, 4000);
  useEffect(() => {
    if (isActiveVisitorLeftDebounced && callStatus === 'active') {
      dispatch(endCall());
    }
  }, [isActiveVisitorLeftDebounced, callStatus, dispatch]);

  useEffect(
    function detectActiveVisitorLeaving() {
      if (wasOnline && !isOnline && !isActiveVisitorLeft) {
        setIsActiveVisitorLeft(true);
      } else if (isOnline && isActiveVisitorLeft) {
        setIsActiveVisitorLeft(false);
      }
    },
    [isOnline, wasOnline, isActiveVisitorLeft, dispatch],
  );

  useEffect(
    function resetActiveVisitorLeftWhenNewVisitor() {
      if (
        activeVisitor?.id &&
        activeVisitor?.id !== prevActiveVisitorId &&
        isActiveVisitorLeft
      ) {
        setIsActiveVisitorLeft(false);
      }
    },
    [activeVisitor?.id, prevActiveVisitorId, isActiveVisitorLeft],
  );

  useEffect(() => {
    if (isConnectedToPeer) {
      setHasConnectedToPeer(true);
    }
  }, [isConnectedToPeer]);

  useEffect(() => {
    if (!activeVisitor?.helpedBy) {
      setIsKickingAdmin(false);
    }
  }, [activeVisitor]);

  const kickAdmin = useCallback(() => {
    if (activeVisitor?.helpedBy) {
      const warning =
        (activeVisitor.isVideo && 'They have an active video call. Are you sure?') ||
        (activeVisitor.isTakeover &&
          'They have an active screen takeover. Are you sure?') ||
        '';
      const kick = () => {
        setIsKickingAdmin(true);
        const { helpedBy, sbid } = activeVisitor;
        dispatch(kickAgentFromVisitor({ agentId: helpedBy, visitorId: sbid }));
        return false; // close dialog when is onOk
      };
      if (warning) {
        Modal.confirm({
          icon: null,
          content: warning,
          okText: 'Replace',
          cancelText: 'Cancel',
          onOk: kick,
        });
      } else {
        kick();
      }
    }
  }, [activeVisitor, dispatch]);

  const retryConnect = useCallback(() => dispatch(pauseVisitorAssociation(false)), [
    dispatch,
  ]);

  const userConnect = useCallback(() => dispatch(pauseVisitorAssociation(false)), [
    dispatch,
  ]);
  const cancelConnect = useCallback(() => dispatch(pauseVisitorAssociation(true)), [
    dispatch,
  ]);

  const goToBilling = useCallback(() => {
    contactSales({ subject: 'Upgrade Plan: Screen Viewing Usage Limit Reached' });
  }, []);

  const isVisitorOnline = !!activeVisitor?.sid;
  const isVisitorOnlineUnknown = !haveVisitorsLoaded;
  const isHelpedByOther = associationStatus === 'busy';
  const wasKicked = associationStatus === 'kicked';
  const isAssociationPaused = associationStatus === 'paused';

  const isConnecting =
    !isHelpedByOther &&
    (isVisitorOnline || isVisitorOnlineUnknown) &&
    (isUsingRrweb ? isAwaitingReplayerFirstRender : !isConnectedToPeer) &&
    !isShowingSpyViewingsUsageLimit;
  let connectingMsg =
    (isVisitorOnlineUnknown && 'Checking if visitor is online...') ||
    (!isConnectedToPeer && 'Establishing peer connection...');
  if (isUsingRrweb) {
    connectingMsg =
      connectingMsg ||
      (isAwaitingInitialSnapshot && 'Waiting for page...') ||
      (isAwaitingReplayerFirstRender && 'Rendering...');
  }

  const statusMessage = useMemo(() => {
    let statusMessage: {
      icon?: ActiveVisitorStatusIcon;
      title: string;
      text?: string;
      btnText?: string;
      btnLoading?: boolean;
      onBtn?: () => void;
    } | null = null;

    // No status messages during tour
    if (isTouring) {
      return statusMessage;
    }

    if (isViewingSelf) {
      statusMessage = {
        icon: 'failed',
        title: 'Hey, stop destroying the universe',
        text: 'You can’t view yourself, or else weird things start to happen.',
      };
    } else if (isShowingSpyViewingsUsageLimit) {
      statusMessage = {
        icon: 'attention',
        title: 'Screen viewing limit reached',
        text:
          'You’ve reached the limit of visitor screen views during this billing period.',
        btnText: 'Upgrade Plan',
        onBtn: goToBilling,
      };
    } else if (isConnecting) {
      statusMessage = {
        icon: 'spinner',
        title: 'Connecting',
        text: connectingMsg || '',
        btnText: 'Cancel',
        onBtn: cancelConnect,
      };
    } else if (wasKicked && !isConnectedToPeer) {
      statusMessage = {
        icon: 'failed',
        title: 'You were disconnected from the visitor.',
        text: 'You were disconnected from the visitor by another agent',
        btnText: 'Try again',
        onBtn: retryConnect || undefined,
      };
    } else if (isActiveVisitorLeft) {
      statusMessage = {
        icon: 'left',
        title: 'Visitor Left',
        text: 'The visitor has left your site.',
      };
    } else if (activeVisitor) {
      if (activeVisitor.sid && isHelpedByOther) {
        statusMessage = {
          title: 'Visitor Busy',
          text: 'The visitor is busy with another agent.',
          btnText: 'Replace their connection',
          btnLoading: isKickingAdmin,
          onBtn: kickAdmin,
        };
      } else if (!activeVisitor.sid && !isConnectedToPeer) {
        statusMessage = {
          title: 'Offline',
          text: 'The visitor is not on your site.',
        };
      } else if (activateVisitorError) {
        statusMessage = {
          icon: 'failed',
          title: 'Activation error',
          text: activateVisitorError,
        };
      } else if (isAssociationPaused) {
        statusMessage = {
          title: 'Online',
          text: 'The visitor is available.',
          btnText: 'Connect',
          onBtn: userConnect,
        };
      }
    }

    return statusMessage;
  }, [
    activateVisitorError,
    activeVisitor,
    cancelConnect,
    connectingMsg,
    goToBilling,
    isActiveVisitorLeft,
    isAssociationPaused,
    isConnectedToPeer,
    isConnecting,
    isHelpedByOther,
    isKickingAdmin,
    isViewingSelf,
    kickAdmin,
    retryConnect,
    userConnect,
    wasKicked,
    isTouring,
    isShowingSpyViewingsUsageLimit,
  ]);

  return {
    statusMessage,
    hasConnectedToPeer,
    isKickingAdmin,
    isActiveVisitorLeft,
  };
};

export const useIsCallIntent = () => {
  const { search } = useLocation();
  const isCallIntent =
    new URLSearchParams(search).get(CALL_PARAM) !== null ||
    new URLSearchParams(search).get(AUDIO_CALL_PARAM) !== null;
  return isCallIntent;
};

/**
 * Functionality for triggering a call to the active visitor if ?call is set.
 */
export const useTriggerCallQueryParam = (visitorId: string) => {
  const { replace } = useHistory();
  const dispatch = useAppDispatch();
  const { peerStatus } = useAppSelector(state => state.realtime);
  const [hasTriggeredCall, setHasTriggeredCall] = useState(false);
  const activeConversationId = useAppSelector(s => s.conversation.conversation?.id);

  const isCallIntent = useIsCallIntent();
  const shouldTriggerCall =
    isCallIntent &&
    !hasTriggeredCall &&
    peerStatus === 'connected' &&
    activeConversationId;

  // Reset with each visitorId change
  useEffect(() => {
    setHasTriggeredCall(false);
  }, [visitorId]);

  useEffect(() => {
    if (shouldTriggerCall) {
      setHasTriggeredCall(true);

      const l = window.location;
      const params = new URLSearchParams(l.search);

      params.delete(CALL_PARAM);

      dispatch(
        startCall({
          activeConversationId,
          video: !params.toString().includes('call-audio'),
        }),
      );
      params.delete(AUDIO_CALL_PARAM);
      const paramsStr = (x => (x ? `?${x}` : ''))(params.toString());
      replace(`${l.pathname}${paramsStr}`);
    }
  }, [shouldTriggerCall, dispatch, replace, activeConversationId]);
};

/**
 * Trigger various notifications for call behaviors.
 */
export const useCallStateNotifications = () => {
  const { callStatusMsg } = useAppSelector(s => s.realtime);
  const callStatus = useAppSelector(selectCallStatus);
  const activeVisitor = useAppSelector(selectActiveVisitor);

  // notify of call rejection or failure
  useEffect(() => {
    if (callStatus === 'rejected') {
      notification.info({
        key: 'call-rejected',
        message: 'Visitor declined the call',
        duration: 6,
      });
    } else if (callStatus === 'failed') {
      notification.info({
        key: 'call-failed',
        message: callStatusMsg || 'Call failed for unknown reason',
        duration: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus]);

  // notify of video request
  useEffect(() => {
    if (
      callStatus === 'active' &&
      activeVisitor?.isVisitorRequestingVideo &&
      !activeVisitor?.isVisitorVideoAllowed
    ) {
      notification.info({
        key: 'visitor-video-request',
        message: 'Visitor requesting video permission',
        duration: 6,
      });
    } else {
      notification.close('visitor-video-request');
    }
  }, [activeVisitor, callStatus]);
};

/**
 * Trigger various notifications for takeover behaviors.
 */
export const useTakeoverStateNotifications = () => {
  const takeoverStatus = useAppSelector(selectTakeoverStatus);

  // notify of takeover rejection or failure
  useEffect(() => {
    if (takeoverStatus === 'rejected') {
      notification.info({
        message: 'Visitor declined the takeover request',
        duration: 6,
      });
    } else if (takeoverStatus === 'failed') {
      notification.info({ message: 'Takeover failed', duration: 6 });
    }
  }, [takeoverStatus]);
};

/**
 * Should we prevent the user from navigating away
 */
export const useLeaveWarning = () => {
  const activeVisitor = useAppSelector(selectActiveVisitor);
  const associationStatus = useAppSelector(s => s.realtime.associationStatus);
  const isCallWantedPending = useAppSelector(s => s.realtime.isCallWantedPending);

  const isAssociated = associationStatus === 'active';
  const isBusyWithAnotherAgent = associationStatus === 'busy';

  const leaveWarning = useMemo(() => {
    if (!isBusyWithAnotherAgent) return null;

    const busyWith =
      isAssociated && isCallWantedPending
        ? 'call attempt'
        : activeVisitor?.isTakeover
        ? 'takeover'
        : null;
    if (busyWith) {
      return `You have an active ${busyWith}, do you want to leave?`;
    }
  }, [
    isBusyWithAnotherAgent,
    isAssociated,
    isCallWantedPending,
    activeVisitor?.isTakeover,
  ]);

  return leaveWarning;
};

/**
 * Provide hook for visitor contextual recordings
 */
export const useVisitorContextualRecordings = () => {
  const isContextualRecordingGatewayActive = useIsGatewayActivated(
    Gateway.CONTEXTUAL_RECORDING,
  );
  const dispatch = useAppDispatch();
  const { activeVisitorId, contextualRecordingsMap } = useAppSelector(s => s.dashboard);

  const contextualRecordings =
    (activeVisitorId && contextualRecordingsMap[activeVisitorId]) || undefined;

  const prevActiveVisitorId = usePrevious(activeVisitorId);
  const [recordings, setRecordings] = useState<Array<VisitorContextualRecording>>([]);
  const [hasMore, setHasMore] = useState(false);

  // Load first page always
  useEffect(() => {
    if (
      !isContextualRecordingGatewayActive ||
      !activeVisitorId ||
      !validate(activeVisitorId)
    )
      return;
    const hasPages = !!contextualRecordings?.pages.length;
    const isStale =
      !!contextualRecordings?.loadTime &&
      Date.now() - contextualRecordings.loadTime > 60 * 1000;
    (async function () {
      if (
        activeVisitorId &&
        activeVisitorId !== prevActiveVisitorId &&
        !contextualRecordings?.isLoading &&
        (!hasPages || isStale)
      ) {
        // try to get first page if no pages exist
        if (hasPages && isStale) {
          log.debug(
            `Contextual recordings for (sbid=${activeVisitorId}) are stale, reloading fresh data`,
          );
        }
        await dispatch(
          fetchMoreContextualRecordings({ sbid: activeVisitorId, reset: isStale }),
        );
      }
    })();
  }, [
    isContextualRecordingGatewayActive,
    prevActiveVisitorId,
    activeVisitorId,
    contextualRecordings,
    dispatch,
  ]);

  useEffect(() => {
    if (!contextualRecordings?.pages) return;
    const recs = contextualRecordings.pages.reduce((acc, page) => {
      return [...acc, ...page.items];
    }, [] as Array<VisitorContextualRecording>);
    setRecordings(recs);
    if (contextualRecordings.pages.length) {
      const lastPage = contextualRecordings.pages[contextualRecordings.pages.length - 1];
      if (lastPage) {
        setHasMore(lastPage.page * lastPage.pageSize < lastPage.total);
      }
    }
  }, [contextualRecordings?.pages]);

  const loadMore = async () => {
    if (!activeVisitorId) return;
    await dispatch(fetchMoreContextualRecordings({ sbid: activeVisitorId }));
  };

  const setRecordingIndexToPlay = (index: number) => {
    dispatch(updateContextualRecordingToPlay(index));
  };

  const areRecordingsAvailable =
    isContextualRecordingGatewayActive &&
    contextualRecordings?.loadTime &&
    !!recordings.length;

  return {
    isFeatureEnabled: isContextualRecordingGatewayActive,
    isLoading: !!contextualRecordings?.isLoading,
    isLoaded: !!contextualRecordings?.loadTime,
    error: contextualRecordings?.error || '',
    recordingIndexToPlay: contextualRecordings?.indexToPlay || 0,
    areRecordingsAvailable,
    recordings,
    hasMore,
    loadMore,
    setRecordingIndexToPlay,
  };
};
